import React from "react";
import { motion } from "framer-motion";
import { Volleyball, Target, ArrowRight, Ban } from "lucide-react";
import ConnectionMotif from "@/components/ConnectionMotif";
import FiberUnderline from "@/components/FiberUnderline";

const PADEL_IMG =
  "https://images.unsplash.com/photo-1689942963385-f5bd03f3b270?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const BEACH_IMG =
  "https://images.unsplash.com/photo-1619296094543-99aca1aa1f9e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

function ChallengeCard({ img, icon: Icon, title, text, full, accent, onSelect, index, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ delay: index * 0.15, duration: 0.6 }}
      className="group relative overflow-hidden rounded-3xl border border-white/10"
      data-testid={testid}
    >
      <div className="absolute inset-0">
        <img
          src={img}
          alt={title}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
        <div
          className="absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
          style={{ boxShadow: `inset 0 0 120px ${accent}55` }}
        />
      </div>

      <div className="relative z-10 flex min-h-[440px] flex-col justify-end p-8">
        <div className="mb-auto flex items-center justify-between">
          <div
            className="grid h-12 w-12 place-items-center rounded-xl"
            style={{ background: `${accent}22`, color: accent, boxShadow: `0 0 22px ${accent}55` }}
          >
            <Icon size={24} />
          </div>
          <span
            className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${
              full
                ? "bg-[#e60000]/20 text-[#ff6b6b]"
                : "bg-white/10 text-white/80 backdrop-blur"
            }`}
            data-testid={`${testid}-badge`}
          >
            {full ? "Posti esauriti" : "Posti limitati"}
          </span>
        </div>

        <h3 className="mt-6 font-display text-2xl font-extrabold tracking-tight sm:text-3xl">{title}</h3>
        <p className="mt-3 max-w-md text-sm text-white/70">{text}</p>

        <button
          onClick={() => onSelect()}
          disabled={full}
          className={`mt-6 flex w-fit items-center gap-2 rounded-full px-6 py-3 font-display text-xs font-extrabold uppercase tracking-[0.12em] transition-all ${
            full
              ? "cursor-not-allowed border border-white/10 text-white/40"
              : "btn-neon"
          }`}
          data-testid={`${testid}-btn`}
        >
          {full ? (
            <>
              <Ban size={16} /> Posti esauriti
            </>
          ) : (
            <>
              Voglio partecipare <ArrowRight size={16} />
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
}

export default function ChallengesSection({ stats, onSelectTournament }) {
  return (
    <section className="relative isolate mx-auto w-full max-w-7xl overflow-hidden px-6 py-24 md:px-10 md:py-32" data-testid="challenges-section">
      <ConnectionMotif color="#e60000" color2="#f5c542" className="-z-10 opacity-[0.12]" />
      <div className="speed-line mb-16" />
      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="text-xs font-semibold uppercase tracking-[0.3em] text-[#e60000]"
      >
        Le Sfide
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mt-4 font-display text-3xl font-black tracking-tight sm:text-4xl lg:text-5xl"
      >
        SCEGLI LA TUA SFIDA
      </motion.h2>
      <FiberUnderline />

      <div className="mt-14 grid gap-6 lg:grid-cols-2">
        <ChallengeCard
          testid="challenge-padel"
          img={PADEL_IMG}
          icon={Target}
          title="Torneo di Padel"
          text="Strategia, velocità e intesa con il tuo compagno. Scendi in campo e gioca per la vittoria."
          full={stats?.padel_full}
          accent="#f5c542"
          index={0}
          onSelect={() => onSelectTournament("padel")}
        />
        <ChallengeCard
          testid="challenge-beach"
          img={BEACH_IMG}
          icon={Volleyball}
          title="Torneo di Beach Volley"
          text="Energia, squadra e divertimento. Forma il tuo team e preparati a giocare sulla sabbia."
          full={stats?.beach_full}
          accent="#e60000"
          index={1}
          onSelect={() => onSelectTournament("beach")}
        />
      </div>

      <div className="mt-8 flex flex-col items-center justify-center gap-3 rounded-2xl border border-white/10 bg-[#0f0f13] p-6 text-center sm:flex-row sm:justify-between sm:text-left">
        <p className="text-sm text-white/60">
          Preferisci vivere la convention senza gareggiare? Puoi partecipare comunque.
        </p>
        <button
          onClick={() => onSelectTournament("nessuno")}
          className="rounded-full border border-white/20 px-6 py-3 text-xs font-extrabold uppercase tracking-[0.12em] text-white transition-colors hover:border-white/50 hover:bg-white/5"
          data-testid="challenge-none-btn"
        >
          Partecipa senza tornei
        </button>
      </div>
    </section>
  );
}
